import { useState, useEffect } from 'react';
import { eventsApi } from '../../lib/api';

const cardStyle = { background: 'rgba(255,255,255,0.97)', border: '1px solid #E2E8F0', borderRadius: 20, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', padding: 24 };

const NDA_STYLES = {
  pending: { bg: '#F1F5F9', color: '#64748B', label: 'Pending' },
  sent: { bg: '#FFFBEB', color: '#F59E0B', label: 'Sent' },
  uploaded: { bg: '#EEF2FF', color: '#4F46E5', label: 'Uploaded' },
  approved: { bg: '#ECFDF5', color: '#10B981', label: 'Approved' },
  rejected: { bg: '#FEF2F2', color: '#F97066', label: 'Rejected' },
  countersigned: { bg: '#ECFDF5', color: '#10B981', label: 'Countersigned' },
  acknowledged: { bg: '#ECFDF5', color: '#10B981', label: 'Acknowledged' },
};

const TERMINAL_STATUSES = ['approved', 'countersigned', 'acknowledged', 'rejected'];
const STALE_DAYS_THRESHOLD = 3;

export default function NDATracker({ eventId }) {
  const [ndas, setNdas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null); // ndaId being retried
  const [retryError, setRetryError] = useState(null); // ndaId that failed twice

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadNdas(); }, [eventId]);

  const loadNdas = async () => {
    try { const data = await eventsApi.getNdas(eventId); setNdas(data.ndas || []); }
    catch (err) { console.error('NDATracker load error:', err); }
    setLoading(false);
  };

  const handleApprove = async (ndaId) => {
    try { await eventsApi.updateNda(eventId, ndaId, { nda_status: 'approved' }); await loadNdas(); }
    catch (err) { console.error('NDATracker approve error:', err); }
  };

  const handleReject = async (ndaId) => {
    try { await eventsApi.updateNda(eventId, ndaId, { nda_status: 'rejected' }); await loadNdas(); }
    catch (err) { console.error('NDATracker reject error:', err); }
  };

  const handleRetryNda = async (ndaId) => {
    setRetrying(ndaId);
    setRetryError(null);
    try {
      await eventsApi.retryNda(eventId, ndaId);
      await loadNdas();
    } catch (err) {
      console.error('NDA retry failed:', err);
      setRetryError(ndaId);
    }
    setRetrying(null);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 48, color: '#94A3B8' }}>Loading...</div>;

  const grouped = {
    uploaded: ndas.filter(n => n.nda_status === 'uploaded'),
    sent: ndas.filter(n => n.nda_status === 'sent'),
    pending: ndas.filter(n => n.nda_status === 'pending'),
    approved: ndas.filter(n => n.nda_status === 'approved'),
    rejected: ndas.filter(n => n.nda_status === 'rejected'),
    countersigned: ndas.filter(n => n.nda_status === 'countersigned'),
    acknowledged: ndas.filter(n => n.nda_status === 'acknowledged'),
  };

  const renderNdaCard = (nda) => {
    const st = NDA_STYLES[nda.nda_status] || NDA_STYLES.pending;

    // Stale calculation: use updated_at or sent_at — NOT lifecycle_updated_at (not on event_nda_tracking)
    const staleTimestamp = nda.updated_at || nda.sent_at;
    const staleDays = staleTimestamp
      ? Math.floor((Date.now() - new Date(staleTimestamp).getTime()) / (24 * 60 * 60 * 1000))
      : 0;
    const isStale = staleDays >= STALE_DAYS_THRESHOLD && !TERMINAL_STATUSES.includes(nda.nda_status);

    return (
      <div key={nda.id} style={{ background: 'rgba(255,255,255,0.97)', border: '1px solid #E2E8F0', borderRadius: 16, padding: 16, marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: 14, fontWeight: 600, color: '#0F172A' }}>
              {nda.event_submissions?.inventor_name} → {nda.companies?.name}
            </div>
            <div style={{ fontSize: 12, color: '#94A3B8', fontFamily: 'Poppins, sans-serif', marginTop: 2 }}>
              {nda.event_submissions?.product_name}
              {nda.uploaded_at && ` · Uploaded ${new Date(nda.uploaded_at).toLocaleDateString()}`}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* Stale alert badge */}
            {isStale && (
              <span style={{
                background: '#FFFBEB', color: '#F59E0B', borderRadius: 9999,
                padding: '4px 8px', fontSize: 11, fontWeight: 600, fontFamily: 'Poppins, sans-serif'
              }}>Stale {staleDays}d</span>
            )}

            {nda.signed_nda_url && (
              <a href={nda.signed_nda_url} target="_blank" rel="noreferrer" style={{
                padding: '4px 12px', borderRadius: 8, background: '#EEF2FF', color: '#4F46E5',
                fontSize: 11, fontWeight: 600, fontFamily: 'Poppins, sans-serif', textDecoration: 'none',
              }}>View Signed NDA</a>
            )}

            {/* Status badge */}
            <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: st.bg, color: st.color, fontFamily: 'Poppins, sans-serif' }}>
              {st.label}
            </span>

            {/* Retry button on failed NDAs */}
            {nda.failed_at && retryError !== nda.id && (
              <button
                onClick={() => handleRetryNda(nda.id)}
                disabled={retrying === nda.id}
                style={{
                  padding: '4px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: '#FEF2F2', color: '#F97066', fontSize: 11, fontWeight: 600,
                  fontFamily: 'Poppins, sans-serif', opacity: retrying === nda.id ? 0.7 : 1,
                }}
              >
                {retrying === nda.id ? 'Retrying...' : 'Retry'}
              </button>
            )}

            {/* Retry failed message */}
            {retryError === nda.id && (
              <span style={{ fontSize: 11, color: '#F97066', fontFamily: 'Poppins, sans-serif' }}>
                Failed again — contact support
              </span>
            )}

            {/* Approve/reject actions for uploaded NDAs */}
            {nda.nda_status === 'uploaded' && (
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => handleApprove(nda.id)} style={{
                  width: 28, height: 28, borderRadius: 8, background: '#ECFDF5', color: '#10B981',
                  border: 'none', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>&#10003;</button>
                <button onClick={() => handleReject(nda.id)} style={{
                  width: 28, height: 28, borderRadius: 8, background: '#FEF2F2', color: '#F97066',
                  border: 'none', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>&#10005;</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <h2 style={{ fontFamily: '"EB Garamond", serif', fontSize: 22, color: '#0F172A', margin: '0 0 16px' }}>
        NDA Tracker ({ndas.length})
      </h2>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 12, marginBottom: 24 }}>
        {Object.entries(NDA_STYLES).map(([key, style]) => (
          <div key={key} style={{ ...cardStyle, padding: 16, textAlign: 'center' }}>
            <div style={{ fontFamily: '"EB Garamond", serif', fontSize: 28, fontWeight: 700, color: style.color }}>
              {grouped[key]?.length || 0}
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: style.color, fontFamily: 'Poppins, sans-serif' }}>{style.label}</div>
          </div>
        ))}
      </div>

      {ndas.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 48 }}>
          <p style={{ fontSize: 14, color: '#94A3B8', fontFamily: 'Poppins, sans-serif' }}>
            No NDAs in progress. Assignments must be published before NDA flow begins.
          </p>
        </div>
      ) : (
        <>
          {/* Needs attention first */}
          {grouped.uploaded.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontFamily: 'Poppins, sans-serif', fontSize: 14, fontWeight: 600, color: '#4F46E5', marginBottom: 8 }}>
                Needs Review ({grouped.uploaded.length})
              </h3>
              {grouped.uploaded.map(nda => renderNdaCard(nda))}
            </div>
          )}

          {/* All others */}
          {['sent', 'pending', 'approved', 'rejected', 'countersigned', 'acknowledged'].map(status => {
            const items = grouped[status] || [];
            if (items.length === 0) return null;
            const style = NDA_STYLES[status];
            return (
              <div key={status} style={{ marginBottom: 24 }}>
                <h3 style={{ fontFamily: 'Poppins, sans-serif', fontSize: 14, fontWeight: 600, color: style.color, marginBottom: 8, textTransform: 'capitalize' }}>
                  {style.label} ({items.length})
                </h3>
                {items.map(nda => renderNdaCard(nda))}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
